import { apiFetch } from '@/utils'

export interface LinkChallenge {
	address: `0x${string}`
	nonce: string
	message: string
	issuedAt: string
	expiresAt: string
}

export interface LinkedWallet {
	address: `0x${string}`
	verifiedAt: string
}

export interface ExternalSendRequest {
	fromAddress: `0x${string}`
	toAddress: `0x${string}`
	amount: string
	nonce: number
	deadline?: number
	signature: string
}

const parseJson = async (res: Response) => {
	try {
		return await res.json()
	} catch {
		return {}
	}
}

const extractMessage = (body: any, fallback: string) => {
	return (
		body?.error?.message ||
		body?.message ||
		body?.error ||
		fallback
	)
}

export async function requestLinkChallenge(
	address: `0x${string}`
): Promise<LinkChallenge> {
	const res = await apiFetch('/v1/wallets/link/challenge', {
		method: 'POST',
		body: JSON.stringify({ address }),
		withCredentials: true
	})

	const body = await parseJson(res)

	if (!res.ok) {
		throw new Error(
			extractMessage(
				body,
				res.status === 409
					? 'This wallet is already linked to another account.'
					: `Failed to request link challenge (${res.status})`
			)
		)
	}

	const challenge = body?.data ?? body
	return challenge as LinkChallenge
}

export async function verifyLinkChallenge(
	address: `0x${string}`,
	signature: string
): Promise<LinkedWallet> {
	const res = await apiFetch('/v1/wallets/link/verify', {
		method: 'POST',
		body: JSON.stringify({ address, signature }),
		withCredentials: true
	})

	const body = await parseJson(res)

	if (!res.ok) {
		throw new Error(
			extractMessage(body, `Failed to verify wallet link (${res.status})`)
		)
	}

	const wallet = body?.data ?? body
	return wallet as LinkedWallet
}

export async function getLinkedWallets(): Promise<LinkedWallet[]> {
	const res = await apiFetch('/v1/wallets/linked', {
		method: 'GET',
		withCredentials: true
	})

	const body = await parseJson(res)

	if (!res.ok) {
		throw new Error(
			extractMessage(body, `Failed to load linked wallets (${res.status})`)
		)
	}

	const payload = body?.data ?? body
	return (payload?.wallets ?? []) as LinkedWallet[]
}

export async function unlinkWallet(address: `0x${string}`): Promise<void> {
	const res = await apiFetch(
		`/v1/wallets/linked/${address}`,
		{
			method: 'DELETE',
			withCredentials: true
		}
	)

	if (!res.ok) {
		const body = await parseJson(res)
		throw new Error(
			extractMessage(body, `Failed to unlink wallet (${res.status})`)
		)
	}
}

export async function sendExternalTransfer(payload: ExternalSendRequest) {
	return apiFetch('/v1/send/external', {
		method: 'POST',
		body: JSON.stringify(payload),
		withCredentials: true
	})
}
